import { Component, Input, OnInit } from '@angular/core';
import { ColorService } from 'src/app/service/color/color.service';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';

// Live HEX / RGB / CMYK converter embedded inside a blog post body. Reuses the
// color tool's conversion logic (ColorService.hexToRgb / hexToCmyk) so the values
// match the full /tools/color page exactly. The post markdown marks a mount point
// with <!--CC-START-->...<!--CC-END--> around a static fallback (kept for crawlers
// and no-JS readers); BlogPostPageComponent swaps this live component in on the
// client.
@Component( {
  selector: 'app-color-converter',
  templateUrl: './color-converter.component.html',
  styleUrls: [ './color-converter.component.scss' ],
} )
export class ColorConverterComponent implements OnInit {

  // Starting colour (any 3/6 digit hex, with or without a leading #).
  @Input() hex = '#00BAFF';
  // When true, open on the bound [hex] (the post's color) instead of a random color.
  @Input() lockHex = false;

  // Raw text bound to the input so a reader can type freely; the swatch/values
  // only update once it parses to a valid colour.
  text = '';

  rgb = { r: 0, g: 0, b: 0 };
  cmyk = { c: 0, m: 0, y: 0, k: 0 };
  name = '';

  // Fire one engagement event the first time the reader touches the widget,
  // not once per keystroke.
  private tracked = false;

  constructor(
    private colorService: ColorService,
    private analyticsService: AnalyticsService,
  ) {
  }

  private trackUse(): void {
    if ( this.tracked ) {
      return;
    }
    this.tracked = true;
    this.analyticsService.logEvent( 'Blog Widget Used', { widget: 'color-converter' } );
  }

  ngOnInit(): void {
    // Open on the bound [hex] when locked (the post's color); otherwise start from a
    // random named color each load.
    if ( !this.lockHex ) {
      const name = this.colorService.getRandomNamedColor();
      const code = this.colorService.getHexCode( name );
      if ( code ) {
        this.hex = '#' + code.toLowerCase();
      }
    }
    this.text = this.normalizeHex( this.hex );
    this.recompute();
  }

  onText( value: string ): void {
    const raw = ( value || '' ).replace( /[^0-9a-fA-F]/g, '' ).toLowerCase().slice( 0, 6 );
    this.text = '#' + raw;
    this.recompute();
    this.trackUse();
  }

  onPicker( value: string ): void {
    this.text = ( value || '' ).toLowerCase();
    this.recompute();
    this.trackUse();
  }

  // Block non-hex keystrokes outright (control keys and shortcuts pass through).
  onKey( event: KeyboardEvent ): void {
    if ( event.ctrlKey || event.metaKey ) {
      return;
    }
    if ( event.key && event.key.length === 1 && !/[0-9a-fA-F]/.test( event.key ) ) {
      event.preventDefault();
    }
  }

  // Current valid swatch colour (falls back to the last good value while typing).
  get swatch(): string {
    return this.hex;
  }

  get rgbString(): string {
    return `rgb(${ this.rgb.r }, ${ this.rgb.g }, ${ this.rgb.b })`;
  }

  get cmykString(): string {
    return `cmyk(${ this.cmyk.c }%, ${ this.cmyk.m }%, ${ this.cmyk.y }%, ${ this.cmyk.k }%)`;
  }

  // Per-channel weight bars, like the full /tools/color page: RGB (0-255) under
  // the RGB value, CMYK (0-100) under the CMYK value, each in its own half.
  get rgbChannels(): { label: string; pct: number; text: string; color: string }[] {
    const r = this.rgb;
    return [
      { label: 'R', pct: r.r / 255 * 100, text: String( r.r ), color: '#e0584f' },
      { label: 'G', pct: r.g / 255 * 100, text: String( r.g ), color: '#3fb27a' },
      { label: 'B', pct: r.b / 255 * 100, text: String( r.b ), color: '#4a86e8' },
    ];
  }

  get cmykChannels(): { label: string; pct: number; text: string; color: string }[] {
    const c = this.cmyk;
    return [
      { label: 'C', pct: c.c, text: c.c + '%', color: '#22b3c8' },
      { label: 'M', pct: c.m, text: c.m + '%', color: '#cf2e8a' },
      { label: 'Y', pct: c.y, text: c.y + '%', color: '#e6c100' },
      { label: 'K', pct: c.k, text: c.k + '%', color: '#3a3a3a' },
    ];
  }

  private normalizeHex( value: string ): string {
    return '#' + ( value || '' ).trim().replace( /^#/, '' ).toLowerCase();
  }

  private recompute(): void {
    const v = ( this.text || '' ).replace( /[^0-9a-fA-F]/g, '' ).toLowerCase().slice( 0, 6 );
    if ( v.length !== 6 || !this.colorService.isValidHexCode( v ) ) {
      return;
    }
    this.hex = '#' + v;

    const rgb = this.colorService.hexToRgb( v );
    this.rgb = {
      r: rgb.items[ 0 ].value,
      g: rgb.items[ 1 ].value,
      b: rgb.items[ 2 ].value,
    };

    const cmyk = this.colorService.hexToCmyk( v );
    this.cmyk = {
      c: cmyk.items[ 0 ].value,
      m: cmyk.items[ 1 ].value,
      y: cmyk.items[ 2 ].value,
      k: cmyk.items[ 3 ].value,
    };

    this.name = this.colorService.getColorName( v );
  }
}
